import { Alert, Button } from "@mui/material";
import { USER_FACING_ERROR_CODE } from "encamp-shared/src/constants/graphqlErrorCodes";
import { Component, ReactNode } from "react";
import { getMessageForErrorCodes } from "utils/errorUtils";

// Codes whose server message is written for the person using the page. Any
// other error (a 500, a network failure) shows only `message`.
const SHOWN_ERROR_CODES = [USER_FACING_ERROR_CODE, "BAD_USER_INPUT"];

/** Shows a failed query inline with Retry, so the rest of the page stays usable. */
export class QueryErrorBoundary extends Component<
  {
    children: ReactNode;
    onRetry: () => void;
    onClearFilters?: () => void;
    fallback?: ReactNode;
    resetKey?: unknown;
    /** What could not be loaded; shown before any server message. */
    message?: string;
  },
  { error: Error | null; resetKey?: unknown }
> {
  state: { error: Error | null; resetKey?: unknown } = {
    error: null,
    resetKey: this.props.resetKey,
  };
  static getDerivedStateFromProps(props: { resetKey?: unknown }, state: { resetKey?: unknown }) {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  render() {
    if (this.state.error && this.props.fallback !== undefined) return this.props.fallback;
    return this.state.error ? (
      <Alert
        severity="error"
        action={
          <>
            {this.props.onClearFilters && (
              <Button color="inherit" onClick={this.props.onClearFilters}>
                Clear filters
              </Button>
            )}
            <Button color="inherit" onClick={this.props.onRetry}>
              Retry
            </Button>
          </>
        }
      >
        {[
          this.props.message ?? "Unable to load data.",
          getMessageForErrorCodes(this.state.error, SHOWN_ERROR_CODES),
        ]
          .filter(Boolean)
          .join(" ")}
      </Alert>
    ) : (
      this.props.children
    );
  }
}
